import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Download, Loader2, RefreshCw, RotateCcw, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { prepare, preload } from '../socialStudio/engine';
import { loadObraPhotos, loadIA, getFeedTimeZone } from '../socialStudio/socialStudio.api';
import { loadClimaMes, loadReportabilidad } from './reporte.api';
import { useObraTimeZone } from '../obras/hooks/useObraTimeZone';
import { A4, SECCIONES, construirPaginas, dibujarPagina, armarReporte, etapasSugeridas, resumenIA, resumenClima, resumenRep, redactarResumen, avanceGeneral } from './reportEngine';

/* Reporte PDF de avance de obra: una plantilla completa armada con los datos reales de la cámara,
   que el cliente puede ajustar (secciones, textos, avance por etapa) y descargar. */

const PALETAS = ['#2BE88B', '#3D8BFF', '#FF7A00', '#FFC400', '#FF5B5B', '#8B5CF6'];
const FUENTES = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';
const OBS_DEFAULT = 'Continuamos con el cronograma previsto para las próximas semanas.\nEl próximo reporte se emitirá el mes que viene con el avance actualizado.\nAnte cualquier consulta, el equipo de obra queda a disposición.';
const isoHoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

function Pagina({ R, P, width }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return undefined;
    c.width = width; c.height = Math.round(width * A4[1] / A4[0]);
    dibujarPagina(c, R, P);
    // las fotos pueden llegar después: se redibuja un par de veces
    const t1 = setTimeout(() => dibujarPagina(c, R, P), 900), t2 = setTimeout(() => dibujarPagina(c, R, P), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [R, P, width]);
  return <canvas ref={ref} className="w-full h-auto block rounded-lg shadow-md bg-white" style={{ aspectRatio: `${A4[0]}/${A4[1]}` }} />;
}

export default function ReportePDF({ user, customerInfo, identifier }) {
  const feedID = user?.feedID;
  const obraTimeZone = useObraTimeZone(feedID);
  const [base, setBase] = useState(null);
  const [carga, setCarga] = useState({ loading: true, paso: 'Buscando las fotos de la obra…', error: '' });
  const [activas, setActivas] = useState(() => Object.fromEntries(SECCIONES.map((s) => [s.id, true])));
  const [edit, setEdit] = useState(null);
  const [descargando, setDescargando] = useState('');
  const ctxRef = useRef({ user, customerInfo, identifier }); ctxRef.current = { user, customerInfo, identifier };

  useEffect(() => {
    if (!document.querySelector(`link[href="${FUENTES}"]`)) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = FUENTES; document.head.appendChild(l); }
  }, []);

  useEffect(() => {
    if (!feedID) return undefined;
    let alive = true;
    const { user: u, customerInfo: ci, identifier: idf } = ctxRef.current;
    setCarga({ loading: true, paso: 'Buscando las fotos de la obra…', error: '' });
    (async () => {
      try {
        const lat = u?.coordinates?.lat, lng = u?.coordinates?.lng;
        const [photos, ia, clima, rep] = await Promise.all([
          loadObraPhotos(feedID, u?.customerId, (n, total) => alive && setCarga((c) => ({ ...c, paso: `Recorriendo la historia de la obra: ${n} de ${total}` })), 40),
          getFeedTimeZone(feedID).then((tz) => loadIA(idf, u?.customerId, 30, tz)),
          loadClimaMes(lat, lng),
          loadReportabilidad(feedID, 30),
        ]);
        if (!alive) return;
        if (photos.hist.length < 2) { setCarga({ loading: false, paso: '', error: 'pocas' }); return; }
        setCarga((c) => ({ ...c, paso: 'Armando las páginas…' }));
        const logoSrc = ci?.logo || u?.logo || '';
        await Promise.race([preload(photos.hist.slice(0, 4).concat(photos.hist.slice(-2)).map((h) => h.src).concat(logoSrc ? [logoSrc] : [])), new Promise((r) => setTimeout(r, 5000))]);
        preload(photos.hist.map((h) => h.src).concat(photos.hoy.map((h) => h.src)));
        const D = prepare({
          feed: feedID, obra: u?.obraName || u?.Name || 'Obra', cliente: ci?.name || 'Empresa', lugar: u?.city || ci?.city || '', logoSrc, ...photos,
        }, null);
        const b = Object.assign(D, { ia, iaRes: resumenIA(ia), climaRes: resumenClima(clima), repRes: resumenRep(rep) });
        const entrega = (() => { const d = new Date(b.inicio + 'T12:00:00'); d.setDate(d.getDate() + Math.max(540, b.dias + 180)); return d.toISOString().slice(0, 10); })();
        setBase(b);
        setEdit({
          titulo: 'Reporte de avance de obra', subtitulo: [b.cliente, b.lugar].filter(Boolean).join(' · '),
          entrega, etapas: etapasSugeridas(b.inicio, entrega, b.hoyFecha), accent: PALETAS[0], fotos: 24, resumen: null, observaciones: OBS_DEFAULT,
        });
        setCarga({ loading: false, paso: '', error: '' });
      } catch (e) {
        console.error(e);
        if (alive) setCarga({ loading: false, paso: '', error: 'red' });
      }
    })();
    return () => { alive = false; };
  }, [feedID]);

  // datos y páginas (se recalculan con cada cambio; la vista previa se actualiza con una pequeña demora)
  const [editDiferido, setEditDiferido] = useState(null);
  useEffect(() => { const t = setTimeout(() => setEditDiferido(edit), 250); return () => clearTimeout(t); }, [edit]);
  const R = useMemo(() => (base && editDiferido ? armarReporte({ ...base, timeZone: obraTimeZone }, editDiferido) : null), [base, editDiferido, obraTimeZone]);
  const paginas = useMemo(() => (R ? construirPaginas(R, activas) : []), [R, activas]);

  const set = (k, v) => setEdit((e) => ({ ...e, [k]: v }));
  const setEtapa = (i, k, v) => setEdit((e) => ({ ...e, etapas: e.etapas.map((x, j) => (j === i ? { ...x, [k]: v } : x)) }));

  const descargar = async () => {
    if (!R) return;
    setDescargando('Preparando…');
    try {
      await preload(R.registro.map((f) => f.src).concat(R.hoy.map((f) => f.src)).concat([R.heroItem.src]));
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const c = document.createElement('canvas'); c.width = A4[0]; c.height = A4[1];
      for (let i = 0; i < paginas.length; i++) {
        setDescargando(`Armando página ${i + 1} de ${paginas.length}…`);
        dibujarPagina(c, R, paginas[i]);
        if (i) pdf.addPage();
        pdf.addImage(c.toDataURL('image/jpeg', 0.86), 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
        await new Promise((r) => setTimeout(r, 0));
      }
      const nombre = `reporte-${R.obra}-${isoHoy()}`.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
      const a = document.createElement('a'); a.href = URL.createObjectURL(pdf.output('blob')); a.download = nombre + '.pdf';
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success(`Reporte descargado (${paginas.length} páginas)`);
    } catch (e) {
      console.error(e); toast.error('No se pudo armar el PDF. Probá de nuevo en unos segundos.');
    } finally { setDescargando(''); }
  };

  if (!feedID) return <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-600">Esta cámara no tiene fotos asociadas.</div>;
  if (carga.loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-center gap-3 text-gray-700">
          <Loader2 className="animate-spin text-blue-600 shrink-0" size={20} />
          <div><div className="font-semibold">Armando el reporte con los datos de la obra…</div><div className="text-sm text-gray-500">{carga.paso}</div></div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 4 }, (_, i) => <div key={i} className="aspect-[1240/1754] rounded-lg bg-gray-200 animate-pulse" />)}</div>
      </div>
    );
  }
  if (carga.error || !R) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 text-gray-700">
        <div className="font-semibold">{carga.error === 'pocas' ? 'Todavía no hay suficientes fotos para armar el reporte' : 'No pudimos traer los datos de la obra'}</div>
        <div className="text-sm text-gray-500 mt-1">{carga.error === 'pocas' ? 'Cuando la cámara junte algunos días de fotos vas a poder descargar el reporte completo.' : 'Revisá la conexión y volvé a abrir la pestaña.'}</div>
      </div>
    );
  }

  const input = 'block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';
  const disponible = (sec) => !sec.requires || (sec.requires === 'ia' ? !!R.iaRes : sec.requires === 'clima' ? !!R.climaRes : !!R.repRes);

  return (
    <div className="space-y-5">
      {/* barra superior */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full"><FileText size={13} />Reporte PDF · beta</div>
          <span className="text-sm text-gray-500">{paginas.length} páginas · avance general {R.avance}%</span>
        </div>
        <button type="button" onClick={descargar} disabled={!!descargando} className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
          <Loader2 size={16} className={descargando ? 'animate-spin' : 'hidden'} /><Download size={16} className={descargando ? 'hidden' : ''} /><span>{descargando || 'Descargar PDF'}</span>
        </button>
      </div>

      <div className="grid lg:grid-cols-[380px_minmax(0,1fr)] gap-6 items-start">
        {/* configuración */}
        <div className="space-y-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-110px)] lg:overflow-y-auto lg:pr-1">
          <section className="bg-white border border-gray-200 rounded-2xl p-5">
            <h3 className="font-bold text-gray-800">Qué incluir</h3>
            <p className="text-xs text-gray-500 mb-3">Activá o sacá secciones. La portada va siempre.</p>
            <div className="space-y-1">
              {SECCIONES.map((sec) => {
                const ok = disponible(sec), on = ok && activas[sec.id];
                return (
                  <label key={sec.id} className={`flex items-start gap-3 rounded-xl px-2 py-2 ${ok ? 'cursor-pointer hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300'}`}>{on && <Check size={14} />}</span>
                    <input type="checkbox" className="sr-only" disabled={!ok} checked={on} onChange={(e) => setActivas((a) => ({ ...a, [sec.id]: e.target.checked }))} />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-800">{sec.nombre}</span>
                      <span className="block text-xs text-gray-500">{ok ? sec.desc : 'Esta cámara no tiene estos datos todavía'}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {activas.registro && (
              <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                <label htmlFor="rp-fotos" className="text-gray-600">Fotos del registro</label>
                <select id="rp-fotos" value={edit.fotos} onChange={(e) => set('fotos', Number(e.target.value))} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" style={{ height: 36 }}>
                  {[12, 20, 24, 32, 40].map((n) => <option key={n} value={n}>{n} fotos ({Math.ceil(n / 4)} páginas)</option>)}
                </select>
              </div>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
            <h3 className="font-bold text-gray-800">Portada</h3>
            <div><label htmlFor="rp-titulo" className="text-xs font-medium text-gray-600">Título</label><input id="rp-titulo" className={input} style={{ height: 42 }} value={edit.titulo} onChange={(e) => set('titulo', e.target.value)} /></div>
            <div><label htmlFor="rp-sub" className="text-xs font-medium text-gray-600">Subtítulo</label><input id="rp-sub" className={input} style={{ height: 42 }} value={edit.subtitulo} onChange={(e) => set('subtitulo', e.target.value)} /></div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">Color</div>
              <div className="flex gap-2">{PALETAS.map((c) => <button key={c} type="button" aria-label={`Color ${c}`} aria-pressed={edit.accent === c} onClick={() => set('accent', c)} className={`w-8 h-8 rounded-full border-2 ${edit.accent === c ? 'border-gray-800' : 'border-white shadow'}`} style={{ background: c }} />)}</div>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-gray-800">Avance por etapa</h3>
              <button type="button" onClick={() => setEdit((e) => ({ ...e, etapas: etapasSugeridas(base.inicio, e.entrega, base.hoyFecha) }))} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"><RotateCcw size={12} />Recalcular</button>
            </div>
            <div><label htmlFor="rp-entrega" className="text-xs font-medium text-gray-600">Entrega estimada</label>
              <input id="rp-entrega" type="date" className={input} style={{ height: 42 }} value={edit.entrega} onChange={(e) => e.target.value && setEdit((x) => ({ ...x, entrega: e.target.value, etapas: etapasSugeridas(base.inicio, e.target.value, base.hoyFecha) }))} /></div>
            <div className="space-y-3">
              {edit.etapas.map((e, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <input aria-label="Nombre de la etapa" className={`${input} flex-1`} style={{ height: 36 }} value={e.nombre} onChange={(ev) => setEtapa(i, 'nombre', ev.target.value)} />
                    <span className="text-sm font-semibold text-gray-800 w-12 text-right tabular-nums">{e.pct}%</span>
                  </div>
                  <input type="range" min={0} max={100} step={5} aria-label={`Avance de ${e.nombre}`} value={e.pct} onChange={(ev) => setEtapa(i, 'pct', Number(ev.target.value))} className="w-full accent-blue-600" />
                </div>
              ))}
            </div>
            <div className="text-sm text-gray-700">Avance general: <b>{avanceGeneral(edit.etapas)}%</b></div>
          </section>

          <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-gray-800">Resumen ejecutivo</h3>
              <button type="button" onClick={() => set('resumen', redactarResumen(armarReporte(base, { ...edit, resumen: '' })))} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"><RefreshCw size={12} />Redactar de nuevo</button>
            </div>
            <textarea aria-label="Resumen ejecutivo" className={`${input} resize-y`} style={{ height: 220, lineHeight: 1.55 }} value={edit.resumen != null ? edit.resumen : R.resumen} onChange={(e) => set('resumen', e.target.value)} />
            <h3 className="font-bold text-gray-800 pt-2">Observaciones y próximos pasos</h3>
            <textarea aria-label="Observaciones" className={`${input} resize-y`} style={{ height: 140, lineHeight: 1.55 }} value={edit.observaciones} onChange={(e) => set('observaciones', e.target.value)} />
            <p className="text-xs text-gray-500">Cada renglón es un punto en la página de cierre.</p>
          </section>
        </div>

        {/* vista previa */}
        <div className="grid sm:grid-cols-2 gap-5">
          {paginas.map((P) => (
            <div key={`${P.id}-${P.n}`} className="space-y-1.5">
              <Pagina R={R} P={P} width={620} />
              <div className="text-xs text-gray-500 text-center">Página {P.n} · {P.id === 'portada' ? 'Portada' : (P.kicker || '').replace(/^\d+ · /, '')}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
